import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow, TableFooter } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { Shield, Upload, FileText, Trash2, Download, CheckCircle2, AlertCircle, Search, ChevronDown, ChevronRight, X } from 'lucide-react';
import axios from 'axios';
import { formatDate } from '@/lib/utils';

const API = process.env.REACT_APP_BACKEND_URL;
const getToken = () => localStorage.getItem('token');
const headers = () => ({ Authorization: `Bearer ${getToken()}` });

const CATEGORIES = [
  { key: 'Compliance Checklist', icon: '1', color: 'blue', note: 'Please tick only the documents included on your compliance package. These are documents that you have submitted to the lenders as well as all mandatory compliance documents.' },
  { key: 'Application', icon: '2', color: 'emerald', note: 'Included are signed application form if available, All Mortgage Commitments including old and latest revisions and Credit Consent and Amortization Schedule.' },
  { key: 'Disclosure and Authorization Documents', icon: '3', color: 'amber', note: 'AML Sanction Screening, MB, AB or SK Disclosure Statement, PEP Declaration, Material Risk, Borrower AML Risk Assessment and KYC.' },
  { key: 'Other Documents', icon: '4', color: 'slate', note: 'All other documents not yet uploaded.' }
];

export default function Compliance() {
  const [summary, setSummary] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedComm, setSelectedComm] = useState(null);
  const [complianceDetail, setComplianceDetail] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [filterStatus, setFilterStatus] = useState('all');

  useEffect(() => { loadSummary(); }, []);

  const loadSummary = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/api/compliance/summary`, { headers: headers() });
      setSummary(res.data);
    } catch (err) {
      toast.error('Failed to load compliance data');
    } finally { setLoading(false); }
  };

  const loadDetail = async (commissionId) => {
    try {
      const res = await axios.get(`${API}/api/compliance/commission/${commissionId}`, { headers: headers() });
      setComplianceDetail(res.data);
    } catch (err) {
      toast.error('Failed to load compliance details');
    }
  };

  const handleUpload = async (commissionId, category, file) => {
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('category', category);
      await axios.post(`${API}/api/compliance/commission/${commissionId}/upload`, formData, {
        headers: { ...headers(), 'Content-Type': 'multipart/form-data' }
      });
      toast.success(`Uploaded to "${category}"`);
      await loadDetail(commissionId);
      loadSummary();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Upload failed');
    } finally { setUploading(false); }
  };

  const handleDownload = async (docId, filename) => {
    try {
      const res = await axios.get(`${API}/api/compliance/document/${docId}/download`, {
        headers: headers(), responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error('Download failed');
    }
  };

  const handleDelete = async (docId) => {
    if (!window.confirm('Delete this document?')) return;
    try {
      await axios.delete(`${API}/api/compliance/document/${docId}`, { headers: headers() });
      toast.success('Document deleted');
      if (selectedComm) await loadDetail(selectedComm);
      loadSummary();
    } catch (err) {
      toast.error('Delete failed');
    }
  };

  const openDetail = (comm) => {
    setSelectedComm(comm.commission_id);
    loadDetail(comm.commission_id);
  };

  const filtered = summary.filter(s => {
    const matchSearch = !search || 
      s.dlc_statement_no?.toLowerCase().includes(search.toLowerCase()) ||
      s.description?.toLowerCase().includes(search.toLowerCase()) ||
      s.agent_name?.toLowerCase().includes(search.toLowerCase());
    const matchStatus = filterStatus === 'all' || 
      (filterStatus === 'complete' && s.is_complete) ||
      (filterStatus === 'incomplete' && !s.is_complete);
    return matchSearch && matchStatus;
  });

  const totalComplete = summary.filter(s => s.is_complete).length;

  return (
    <div className="space-y-6" data-testid="compliance-page">
      <div className="flex flex-col gap-1">
        <h1 className="text-2xl font-bold text-slate-900" style={{ fontFamily: 'Manrope' }}>
          Compliance Documents
        </h1>
        <p className="text-sm text-slate-500">Upload and manage compliance documents per commission transaction</p>
      </div>

      {/* Summary Stats */}
      <div className="grid grid-cols-3 gap-4">
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center">
              <FileText className="w-5 h-5 text-blue-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-slate-900">{summary.length}</p>
              <p className="text-xs text-slate-500">Total Commissions</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-100 flex items-center justify-center">
              <CheckCircle2 className="w-5 h-5 text-emerald-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-emerald-600">{totalComplete}</p>
              <p className="text-xs text-slate-500">Fully Compliant</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-100 flex items-center justify-center">
              <AlertCircle className="w-5 h-5 text-amber-600" />
            </div>
            <div>
              <p className="text-2xl font-bold text-amber-600">{summary.length - totalComplete}</p>
              <p className="text-xs text-slate-500">Incomplete</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <div className="flex gap-3 items-center">
        <div className="relative flex-1 max-w-sm">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
          <Input className="pl-9" placeholder="Search by DLC#, description, agent..." value={search} onChange={e => setSearch(e.target.value)} data-testid="compliance-search" />
        </div>
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger className="w-48" data-testid="compliance-filter">
            <SelectValue placeholder="Filter status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All</SelectItem>
            <SelectItem value="complete">Complete</SelectItem>
            <SelectItem value="incomplete">Incomplete</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {/* Commission Table */}
      <Card className="border-slate-200">
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead className="font-semibold">DLC Statement #</TableHead>
                <TableHead className="font-semibold">Description</TableHead>
                <TableHead className="font-semibold">Agent</TableHead>
                <TableHead className="font-semibold">Payment Date</TableHead>
                <TableHead className="text-center font-semibold">Documents</TableHead>
                <TableHead className="text-center font-semibold">Status</TableHead>
                <TableHead className="text-right font-semibold">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">Loading...</TableCell></TableRow>
              ) : filtered.length === 0 ? (
                <TableRow><TableCell colSpan={7} className="text-center py-8 text-slate-500">No commissions found</TableCell></TableRow>
              ) : (
                filtered.map(comm => (
                  <TableRow key={comm.commission_id} className="hover:bg-slate-50">
                    <TableCell className="font-mono text-sm font-medium">{comm.dlc_statement_no || '-'}</TableCell>
                    <TableCell className="text-sm max-w-[200px] truncate">{comm.description || '-'}</TableCell>
                    <TableCell className="text-sm">{comm.agent_name}</TableCell>
                    <TableCell className="text-sm font-mono">{comm.payment_date ? formatDate(comm.payment_date) : '-'}</TableCell>
                    <TableCell className="text-center">
                      <span className="text-sm font-medium">{comm.uploaded_categories}/{comm.total_categories}</span>
                      <span className="text-xs text-slate-400 ml-1">({comm.doc_count} files)</span>
                    </TableCell>
                    <TableCell className="text-center">
                      {comm.is_complete ? (
                        <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100">
                          <CheckCircle2 className="w-3 h-3 mr-1" />Complete
                        </Badge>
                      ) : (
                        <Badge variant="outline" className="text-amber-600 border-amber-200">
                          <AlertCircle className="w-3 h-3 mr-1" />{comm.uploaded_categories}/{comm.total_categories}
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <Button variant="outline" size="sm" onClick={() => openDetail(comm)} data-testid={`manage-${comm.commission_id}`}>
                        <Shield className="w-3.5 h-3.5 mr-1" />Manage
                      </Button>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Detail Modal */}
      <Dialog open={!!complianceDetail} onOpenChange={(open) => { if (!open) { setComplianceDetail(null); setSelectedComm(null); } }}>
        <DialogContent className="max-w-3xl max-h-[85vh] overflow-y-auto" data-testid="compliance-detail-modal">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Manrope' }}>
              <Shield className="w-5 h-5 text-blue-600" />
              Compliance Documents
            </DialogTitle>
            {complianceDetail && (
              <p className="text-sm text-slate-500">
                Commission: {summary.find(s => s.commission_id === selectedComm)?.dlc_statement_no} — {summary.find(s => s.commission_id === selectedComm)?.description}
              </p>
            )}
          </DialogHeader>

          {complianceDetail && (
            <div className="space-y-4">
              {/* Progress */}
              <div className="flex items-center gap-3 p-3 rounded-lg bg-slate-50 border">
                <div className={`text-sm font-semibold ${complianceDetail.summary.is_complete ? 'text-emerald-600' : 'text-amber-600'}`}>
                  {complianceDetail.summary.total_uploaded}/{complianceDetail.summary.total_required} Categories Uploaded
                </div>
                <div className="flex-1 bg-slate-200 rounded-full h-2">
                  <div
                    className={`h-2 rounded-full transition-all ${complianceDetail.summary.is_complete ? 'bg-emerald-500' : 'bg-amber-500'}`}
                    style={{ width: `${(complianceDetail.summary.total_uploaded / complianceDetail.summary.total_required) * 100}%` }}
                  />
                </div>
              </div>

              {/* Category Cards */}
              {CATEGORIES.map(({ key, icon, color, note }) => {
                const catData = complianceDetail.categories[key] || { complete: false, documents: [] };
                return (
                  <Card key={key} className={`border ${catData.complete ? 'border-emerald-200 bg-emerald-50/30' : 'border-slate-200'}`}>
                    <CardContent className="p-4">
                      <div className="flex items-center justify-between mb-2">
                        <div className="flex items-center gap-2">
                          <span className={`w-6 h-6 rounded-full flex items-center justify-center text-xs font-bold ${catData.complete ? 'bg-emerald-500 text-white' : 'bg-slate-200 text-slate-600'}`}>{icon}</span>
                          <h4 className="font-semibold text-sm text-slate-900">{key}</h4>
                          {catData.complete && <CheckCircle2 className="w-4 h-4 text-emerald-500" />}
                        </div>
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            className="hidden"
                            onChange={(e) => {
                              if (e.target.files[0]) handleUpload(selectedComm, key, e.target.files[0]);
                              e.target.value = '';
                            }}
                            disabled={uploading}
                            data-testid={`upload-${key.replace(/\s/g, '-')}`}
                          />
                          <span className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium rounded-md border border-slate-200 bg-white hover:bg-slate-50 transition-colors cursor-pointer">
                            <Upload className="w-3.5 h-3.5" />
                            {uploading ? 'Uploading...' : 'Upload'}
                          </span>
                        </label>
                      </div>
                      <p className="text-xs text-slate-500 italic pl-8 mb-3">{note}</p>

                      {catData.documents.length === 0 ? (
                        <p className="text-xs text-slate-400 italic pl-8">No documents uploaded yet</p>
                      ) : (
                        <div className="space-y-1.5 pl-8">
                          {catData.documents.map(doc => (
                            <div key={doc.id} className="flex items-center justify-between py-1.5 px-3 bg-white rounded border border-slate-100 group">
                              <div className="flex items-center gap-2 min-w-0">
                                <FileText className="w-3.5 h-3.5 text-slate-400 shrink-0" />
                                <span className="text-xs text-slate-700 truncate" title={doc.original_filename}>{doc.original_filename}</span>
                                <span className="text-[10px] text-slate-400 shrink-0">{(doc.file_size / 1024).toFixed(0)}KB</span>
                              </div>
                              <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleDownload(doc.id, doc.original_filename)}>
                                  <Download className="w-3 h-3 text-blue-600" />
                                </Button>
                                <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => handleDelete(doc.id)}>
                                  <Trash2 className="w-3 h-3 text-red-500" />
                                </Button>
                              </div>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
